import * as Sentry from '@sentry/react';
import type { Component } from 'react';

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

if (dsn) {
  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
    // MUST be the same string the Sentry build plugin uploaded sourcemaps under. `vite.config.ts`
    // guarantees that by deriving both from `SENTRY_RELEASE` and assigning this one itself — see
    // `lib/sentryRelease.ts` for why a mismatch here fails silently.
    release: import.meta.env.VITE_SENTRY_RELEASE,
    sendDefaultPii: false,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: parseSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE, 0),
  });
  installCspViolationListener();
}

export function captureReactError(
  error: unknown,
  errorInfo: { componentStack?: string | undefined; errorBoundary?: Component<unknown, {}, any> | undefined },
) {
  Sentry.reactErrorHandler()(error, {
    componentStack: errorInfo.componentStack ?? null,
  });
}

export function captureRecoverableReactError(error: unknown, errorInfo: { componentStack?: string | undefined }) {
  Sentry.reactErrorHandler()(error, {
    componentStack: errorInfo.componentStack ?? null,
  });
}

export function captureApiRequestError({
  path,
  status,
  correlationId,
}: {
  path: string;
  status: number;
  correlationId: string | null;
}) {
  if (!dsn) {
    return;
  }

  Sentry.captureException(new Error(`API request failed: ${path}`), {
    tags: {
      area: 'api-client',
      status: String(status),
    },
    extra: {
      path,
      status,
      correlationId,
    },
  });
}

export async function sendSentryVerificationEvent(marker: string) {
  if (!dsn) {
    return false;
  }

  Sentry.captureException(new Error(`Author Sentry browser verification ${marker}`), {
    tags: {
      area: 'sentry-verification',
      marker,
    },
  });
  await Sentry.flush(2_000);
  return true;
}

function parseSampleRate(value: string | undefined, fallback: number) {
  if (!value) {
    return fallback;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : fallback;
}

function installCspViolationListener() {
  if (typeof window === 'undefined' || window.__mindojoSentryCspListenerInstalled) {
    return;
  }

  window.__mindojoSentryCspListenerInstalled = true;
  window.addEventListener('securitypolicyviolation', (event) => {
    Sentry.captureMessage('Content Security Policy violation', {
      level: 'warning',
      tags: {
        area: 'csp',
        effectiveDirective: event.effectiveDirective,
        violatedDirective: event.violatedDirective,
      },
      extra: {
        blockedURI: event.blockedURI,
        documentURI: event.documentURI,
        disposition: event.disposition,
        lineNumber: event.lineNumber,
        originalPolicy: event.originalPolicy,
        sourceFile: event.sourceFile,
        statusCode: event.statusCode,
      },
    });
  });
}

declare global {
  interface Window {
    __mindojoSentryCspListenerInstalled?: boolean;
  }
}
